import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Input, Modal, Segmented, Select, Space, Switch,
  Tabs, Tooltip, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { GD_TOOLS, TGDDesign, TGraphicDesignLessonConfig, TGDTool } from
  '../../../models/courses/graphicDesign';
import JsonEditor from '../../JsonEditor/JsonEditor';
import GDEditor from '../editor/GDEditor';
import RulesEditor from './RulesEditor';
import ZonesEditor from './ZonesEditor';
import VersionsEditor from './VersionsEditor';
import ExerciseTypePicker from './ExerciseTypePicker';
import AdvancedSettings from './AdvancedSettings';
import ThresholdField from './ThresholdField';
import LocalizedInput, { TAdminLanguage } from './LocalizedInput';
import { adminSteps, isIntentRead, stepIssues, TAdminStep } from './steps';
import { missingTranslations } from './translations';
import { RULE_TYPES_BY_EXERCISE } from './ruleTemplates';
import { validateGraphicDesignConfig } from './validateConfig';
import { checkSolution } from './solutionCheck';
import { MISSION_03_SAMPLE } from './samples/mission03';

interface GraphicDesignLessonAdminProps {
  config: TGraphicDesignLessonConfig;
  onConfigChange: (config: TGraphicDesignLessonConfig) => void;
  onSave: () => void; saving: boolean; dirty: boolean;
  initialStatement?: string; onStatementChange?: (value: string) => void;
  readOnly?: boolean;
}
const languages: TAdminLanguage[] = ['fr', 'en', 'ar'];
const GraphicDesignLessonAdmin: React.FC<GraphicDesignLessonAdminProps> = ({ config,
  onConfigChange, onSave, saving, dirty, initialStatement, onStatementChange, readOnly }) => {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<TAdminLanguage>('fr');
  const [activeStep, setActiveStep] = useState<string>('essentials');
  const steps = adminSteps(config);
  useEffect(() => { if (activeStep !== 'json' && !steps.includes(activeStep as TAdminStep))
    setActiveStep('essentials'); }, [activeStep, steps]);
  const change = (patch: Partial<TGraphicDesignLessonConfig>) => {
    if (!readOnly) onConfigChange({ ...config, ...patch });
  };
  const emptyDesign = (): TGDDesign => ({ background: config.background, components: [] });
  const editorConstraints = { allowedTools: GD_TOOLS };
  const issues = validateGraphicDesignConfig(config);
  const grouped = stepIssues(issues, config);
  const evaluation = checkSolution(config);
  const errors = issues.filter(issue => issue.level === 'error');
  const warnings = issues.filter(issue => issue.level === 'warning');
  const failed = evaluation?.results.filter(result => result.tier === 'required' && !result.passed)
    .map(result => config.rules.find(rule => rule.id === result.ruleId)?.label.fr || result.ruleId);
  const save = () => {
    if (!errors.length) { onSave(); return; }
    Modal.confirm({ title: t('graphicDesignLesson.admin.confirmErrors'),
      content: errors.map(issue => issue.message).join(' · '), onOk: onSave });
  };
  const initial = config.initialState?.mode === 'inline' ? config.initialState.design : emptyDesign();
  const alert = (step: TAdminStep) => grouped[step].length ? <Alert showIcon
    type={grouped[step].some(issue => issue.level === 'error') ? 'error' : 'warning'}
    message={<ul style={{ margin: 0, paddingInlineStart: 20 }}>{grouped[step].map((issue, index) =>
      <li key={index}>{issue.message}</li>)}</ul>} /> : null;
  const hints = <Form layout="vertical">
    <Form.Item label={t('graphicDesignLesson.admin.hintsTitle')}
      extra={t('graphicDesignLesson.admin.hintsHelp')}>
      {config.hints.map((hint, index) => <div className="gd-admin-row" key={index}>
        <div style={{ flex: 1 }}><Form.Item label={t('graphicDesignLesson.admin.hintNumber',
          { n: index + 1 })}><LocalizedInput value={hint} language={language} multiline
            readOnly={readOnly} onChange={next => change({ hints: config.hints.map((old, i) =>
              i === index ? next : old) })} /></Form.Item></div>
        <Button type="text" icon={<DeleteOutlined />} disabled={readOnly}
          aria-label={t('graphicDesignLesson.admin.deleteHint', { n: index + 1 })}
          onClick={() => change({ hints: config.hints.filter((_, i) => i !== index) })} />
      </div>)}
      {config.hints.length < 3 && <Button type="dashed" block disabled={readOnly}
        onClick={() => change({ hints: [...config.hints, { fr: '' }] })}>
        {t('graphicDesignLesson.admin.addHint')}</Button>}
    </Form.Item>
    <Form.Item label={t('graphicDesignLesson.admin.rewardTitle')}>
      <Switch checked={!!config.reward} disabled={readOnly}
        onChange={checked => change({ reward: checked ? { atStars: 2,
          unlockTool: GD_TOOLS.find(tool => !config.allowedTools.includes(tool)) || 'layers' }
          : undefined })} />{' '}{t('graphicDesignLesson.admin.rewardSwitch')}
    </Form.Item>
    {config.reward && <Space wrap>{t('graphicDesignLesson.admin.atStars')}
      <Select value={config.reward.atStars} disabled={readOnly} style={{ width: 80 }}
        options={[2, 3].map(value => ({ value, label: String(value) }))}
        onChange={atStars => change({ reward: { ...config.reward!, atStars } })} />
      {t('graphicDesignLesson.admin.unlock')}
      <Select value={config.reward.unlockTool} disabled={readOnly} style={{ width: 160 }}
        options={GD_TOOLS.map(tool => ({ value: tool,
          label: t(`graphicDesignLesson.editor.tool.${tool}`) }))}
        onChange={(unlockTool: TGDTool) => change({ reward: { ...config.reward!, unlockTool } })} />
    </Space>}
  </Form>;
  const contents: Record<TAdminStep, React.ReactNode> = {
    essentials: <div className="gd-admin-step"><ExerciseTypePicker config={config}
      onChange={change} readOnly={readOnly} />
      <Form layout="vertical">
        <Form.Item label={t('graphicDesignLesson.admin.instruction')}
          extra={t('graphicDesignLesson.admin.instructionHelp')}>
          <Input.TextArea autoSize disabled={readOnly}
            value={initialStatement ?? config.statement ?? ''}
            onChange={event => { onStatementChange?.(event.target.value);
              change({ statement: event.target.value }); }} /></Form.Item>
        <Form.Item label={t('graphicDesignLesson.admin.clientMessage')}
          extra={t('graphicDesignLesson.admin.clientMessageHelp')}>
          <LocalizedInput value={config.brief} language={language} multiline readOnly={readOnly}
            onChange={brief => change({ brief })} /></Form.Item>
        <Form.Item label={t('graphicDesignLesson.admin.clientName')}>
          <Input value={config.clientName ?? ''} disabled={readOnly}
            placeholder={t('graphicDesignLesson.admin.clientExample')}
            onChange={event => change({ clientName: event.target.value })} /></Form.Item>
      </Form><AdvancedSettings config={config} onChange={change} readOnly={readOnly} /></div>,
    start: <><Typography.Paragraph type="secondary">{t(config.exerciseType === 'CREATE'
      ? 'graphicDesignLesson.admin.stepHelp.startCreate'
      : 'graphicDesignLesson.admin.stepHelp.startTransform')}</Typography.Paragraph>
      <GDEditor initialDesign={initial} artboard={config.artboard}
        constraints={editorConstraints} readOnly={readOnly}
        onChange={design => change({ initialState: { mode: 'inline', design } })} /></>,
    solution: <><Typography.Paragraph type="secondary">
      {t('graphicDesignLesson.admin.stepHelp.solution')}</Typography.Paragraph>
      <GDEditor initialDesign={config.solution ?? emptyDesign()} artboard={config.artboard}
        constraints={editorConstraints} readOnly={readOnly}
        onChange={solution => change({ solution })} /></>,
    rules: <div className="gd-admin-step"><ThresholdField value={config.requiredRules}
      total={config.rules.filter(rule => rule.tier === 'required').length}
      label={t('graphicDesignLesson.admin.ruleThreshold')} readOnly={readOnly}
      onChange={requiredRules => change({ requiredRules })} />
      {evaluation && !evaluation.requiredMet && <Alert type="error" showIcon
        message={t('graphicDesignLesson.admin.solutionFails', { rules: failed?.join(', ') })} />}
      <RulesEditor rules={config.rules} onChange={rules => change({ rules })}
        language={language} allowedTypes={RULE_TYPES_BY_EXERCISE[config.exerciseType]}
        solution={config.solution} evaluation={evaluation ?? undefined}
        zoneIds={config.zones?.map(zone => zone.id)}
        versionIds={isIntentRead(config) ? config.choices?.map(choice => choice.id)
          : config.versions?.map(version => version.id)}
        choiceIds={config.choices?.map(choice => choice.id)} readOnly={readOnly} /></div>,
    zones: <><div className="gd-admin-step"><ThresholdField value={config.requiredRules}
      total={config.zones?.length ?? 0} label={t('graphicDesignLesson.admin.zoneThreshold')}
      readOnly={readOnly} onChange={requiredRules => change({ requiredRules })} /></div>
      <ZonesEditor zones={config.zones ?? []} onChange={zones => change({ zones })}
        artboard={config.artboard} design={initial} backgroundImage={config.backgroundImage}
        onBackgroundImage={backgroundImage => change({ backgroundImage })}
        readOnly={readOnly} language={language} /></>,
    versions: <VersionsEditor versions={config.versions ?? []}
      onVersions={versions => change({ versions })} choices={config.choices ?? []}
      onChoices={choices => change({ choices })} rules={config.rules}
      onRules={rules => change({ rules })} proofRules={config.proofRules ?? []}
      onProofRules={proofRules => change({ proofRules })} artboard={config.artboard}
      background={config.background} intent={config.exerciseType === 'INTENT'}
      readOnly={readOnly} language={language} />,
    help: <div className="gd-admin-step">{hints}</div>,
  };
  const stepLabel = (step: TAdminStep) => {
    if (step === 'start') return t(config.exerciseType === 'CREATE'
      ? 'graphicDesignLesson.admin.step.startOptional'
      : 'graphicDesignLesson.admin.step.startTransform');
    if (step === 'solution' && config.exerciseType === 'TRANSFORM')
      return t('graphicDesignLesson.admin.step.solutionTransform');
    if (step === 'versions') return t(config.exerciseType === 'CHOOSE'
      ? 'graphicDesignLesson.admin.step.versionsChoose'
      : 'graphicDesignLesson.admin.step.versionsIntent');
    return t(`graphicDesignLesson.admin.step.${step}`);
  };
  const items = [...steps.map((step, index) => ({ key: step,
    label: <Space size={4}>{`${index + 1}. ${stepLabel(step)}`}
      {grouped[step].length > 0 && <Badge count={grouped[step].length} size="small"
        color={grouped[step].some(issue => issue.level === 'error') ? 'red' : 'orange'} />}</Space>,
    children: <>{alert(step)}{contents[step]}</>,
  })), { key: 'json', label: t('graphicDesignLesson.admin.jsonAdvanced'),
    children: <JsonEditor<TGraphicDesignLessonConfig>
      title={t('graphicDesignLesson.admin.jsonAdvanced')} value={config}
      sample={MISSION_03_SAMPLE} readOnly={readOnly} onApply={onConfigChange} /> }];
  const status = errors.length ? t('graphicDesignLesson.admin.errorCount', { count: errors.length })
    : warnings.length ? t('graphicDesignLesson.admin.warningCount', { count: warnings.length })
      : evaluation && !evaluation.requiredMet ? t('graphicDesignLesson.admin.modelNoWin')
        : evaluation ? t('graphicDesignLesson.admin.readyStars', {
          stars: '★'.repeat(evaluation.stars) + '☆'.repeat(3 - evaluation.stars),
        }) : t('graphicDesignLesson.admin.ready');
  const statusKind: 'error' | 'warning' | 'success' | 'default' = errors.length ||
    (evaluation && !evaluation.requiredMet) ? 'error'
    : warnings.length ? 'warning' : evaluation ? 'success' : 'default';
  return <div className="gd-admin">
    <div className="gd-admin-bar"><Button type="text" onClick={() => {
      const first = steps.find(step => grouped[step].length);
      if (first) setActiveStep(first);
      else if (evaluation && !evaluation.requiredMet)
        setActiveStep(steps.includes('rules') ? 'rules' : 'essentials');
    }}><Badge status={statusKind} /> {status}</Button>
      <Space wrap><span>{t('graphicDesignLesson.admin.textLanguage')}</span>
        <Segmented value={language} onChange={value => setLanguage(value as TAdminLanguage)}
          options={languages.map(value => {
            const count = value === 'fr' ? 0 : missingTranslations(config, value);
            return { value, label: <Tooltip title={count ? t('graphicDesignLesson.admin.toTranslate',
              { count }) : undefined}>{t(`graphicDesignLesson.admin.languages.${value}`)}
              {count > 0 && <Typography.Text type="secondary"> · {count}</Typography.Text>}
            </Tooltip> };
          })} /></Space>
      {!readOnly && <Space className="gd-admin-save" wrap>
        {dirty && <Typography.Text type="secondary">
          {t('graphicDesignLesson.admin.unsaved')}</Typography.Text>}
        <Button type="primary" onClick={save} loading={saving}>
          {t('graphicDesignLesson.admin.save')}</Button></Space>}
    </div>
    <Tabs items={items} activeKey={activeStep} onChange={setActiveStep} destroyInactiveTabPane />
  </div>;
};
export default GraphicDesignLessonAdmin;
